import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { ApiDriverInterfaceService } from '../api-interface/api-driver-interface';
import { ApiInterfaceService } from '../api-interface/api-interface.service';
import { CookieService } from 'ngx-cookie-service';

@Injectable({
    providedIn: 'root',
})
export class UserDataService {
    private referrals = new BehaviorSubject<any>(undefined);
    private userSummary = new BehaviorSubject<any>(null);
    private isUberLinkedSuccessfully = new BehaviorSubject<any>(false);
    userSummaryDetails: any = null;

    constructor(
        private apiInterfaceService: ApiInterfaceService,
        private apiDriverInterfaceService: ApiDriverInterfaceService,
        private cookieService: CookieService
    ) {}

    getUberLinked() {
        return this.isUberLinkedSuccessfully.asObservable();
    }

    setUberLinked(data: any) {
        this.isUberLinkedSuccessfully.next(data);
    }

    getUserSummary() {
        return this.userSummary.asObservable();
    }

    fetchUserSummary(): Observable<any> {
        return this.apiDriverInterfaceService
            .get('driver/summary/', null)
            .pipe(map((data) => this.setUserSummary(data)));
    }

    setUserSummary(data: any) {
        this.userSummaryDetails = data?.data;
        this.userSummary.next(data?.data);
        if (data?.data?.status) {
            localStorage.setItem('status', data.data.status);
        }
        this.cookieService.set('tablet_id', data?.data?.tablet_id);
    }

    getReferralDetails() {
        return this.referrals.asObservable();
    }

    fetchReferralDetails(data: any | null): Observable<any> {
        if (!data) {
            data = {
                complete_limit: 20,
                complete_offset: 0,
                complete_page: 1,
                pending_limit: 20,
                pending_page: 1,
                pending_offset: 0,
                waitlist_limit: 20,
                waitlist_offset: 0,
                waitlist_page: 1,
            };
        }
        return this.apiDriverInterfaceService
            .get('driver/referrals/', data)
            .pipe(map((data) => this.setReferralDetails(data)));
    }

    setReferralDetails(data: any) {
        this.referrals.next(data);
    }

    cashoutEarnings(data: any): Observable<any> {
        return this.apiInterfaceService.post('cashout_earning/', data);
    }

    cashoutAggEarnings(data: any): Observable<any> {
        return this.apiDriverInterfaceService.postV2('driver/cashout/', data);
    }

    deletePrizeOutSession(sessionId: string): Observable<any> {
        return this.apiDriverInterfaceService.delete(
            'driver/prizeout/session',
            sessionId
        );
    }

    fetchDriverStatus(): Observable<any> {
        return this.apiDriverInterfaceService.get('driver/status/', null);
    }

    fetchRecentActivities(): Observable<any> {
        return this.apiDriverInterfaceService.get('driver/activity/', null);
    }

    fetchBadges(): Observable<any> {
        return this.apiDriverInterfaceService.get('driver/badges/', null);
    }

    fetchAgreement(): Observable<any> {
        return this.apiDriverInterfaceService.get('driver/agreement/', null);
    }

    fetchPromos(): Observable<any> {
        return this.apiDriverInterfaceService.get('driver/offer/', null);
    }

    fetchPoints(year?: number): Observable<any> {
        const params = year ? { year } : null;
        return this.apiDriverInterfaceService.get('driver/points/', params);
    }

    fetchPayments(data: any): Observable<any> {
        return this.apiDriverInterfaceService.get('driver/payments/', data);
    }

    fetchPaymentsV2(data: any): Observable<any> {
        return this.apiDriverInterfaceService.getV2('driver/payments/', data);
    }

    fetchPaymentDetails(payload: any = null): Observable<any> {
        return this.apiDriverInterfaceService.get(
            'driver/payment_method/',
            payload
        );
    }

    fetchProfile(): Observable<any> {
        return this.apiDriverInterfaceService.get('driver/profile/', null);
    }

    initiatePrizeoutSession(data: any): Observable<any> {
        return this.apiDriverInterfaceService.post(
            'driver/prizeout/session/',
            data
        );
    }

    validateCardData(): Observable<any> {
        return this.apiDriverInterfaceService.postV2(
            'driver/validate_cashout_earning/',
            {}
        );
    }

    savePaymentDetails(payload: any): Observable<any> {
        return this.apiDriverInterfaceService.post(
            'driver/payment_method/',
            payload
        );
    }

    saveStripeToSAcceptence(data: any = null): Observable<any> {
        return this.apiDriverInterfaceService.post(
            'driver/stripe_tos_acceptance/',
            data
        );
    }

    getOTP(reason: any): Observable<any> {
        return this.apiDriverInterfaceService.get(
            `driver/otp/?otp_reason=${reason}`,
            null
        );
    }

    validateOTP(payload: any): Observable<any> {
        return this.apiDriverInterfaceService.post('driver/otp/', payload);
    }

    logoutDriver(): Observable<any> {
        return this.apiDriverInterfaceService.post('user/logout/', {});
    }

    fetchJourneyTVTracker(): Observable<any> {
        return this.apiDriverInterfaceService.get('driver/uber/journey-tv/tracker/', null);
    }

    fetchJourneyTVAggregated(year?: number): Observable<any> {
        const params = year ? { year } : null;
        return this.apiDriverInterfaceService.get('driver/uber/journey-tv/aggregated/', params);
    }
}
